import React, { useMemo } from "react";
import { Box, Button, Modal, ModalDialog, Typography } from "@mui/joy";
import { theme } from "styles/theme";
import type { TaskEditorState } from "../utils/taskEditorUtils";

interface TurnDiffModalProps {
  open: boolean;
  taskBefore: TaskEditorState;
  taskAfter: TaskEditorState;
  onClose: () => void;
}

type DiffLine = { kind: "same" | "add" | "del"; text: string };

// 같은 내용인데 object key 순서만 다르면 noise diff 가 폭증 — stringify 단계에서 정규화.
function stableStringify(value: unknown): string {
  return JSON.stringify(
    value,
    (_, v) => {
      if (v && typeof v === "object" && !Array.isArray(v)) {
        const sorted: Record<string, unknown> = {};
        for (const k of Object.keys(v).sort()) {
          sorted[k] = (v as Record<string, unknown>)[k];
        }
        return sorted;
      }
      return v;
    },
    2
  );
}

// 라이브러리 회피용 line-level LCS.
function computeLineDiff(beforeText: string, afterText: string): DiffLine[] {
  const a = beforeText.split("\n");
  const b = afterText.split("\n");
  const n = a.length;
  const m = b.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () =>
    new Array(m + 1).fill(0)
  );
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      if (a[i] === b[j]) dp[i][j] = dp[i + 1][j + 1] + 1;
      else dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ kind: "same", text: a[i] });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      out.push({ kind: "del", text: a[i] });
      i++;
    } else {
      out.push({ kind: "add", text: b[j] });
      j++;
    }
  }
  while (i < n) out.push({ kind: "del", text: a[i++] });
  while (j < m) out.push({ kind: "add", text: b[j++] });
  return out;
}

const COLOR_ADD = "#15803d";
const COLOR_DEL = "#b91c1c";
const BG_ADD = "rgba(34, 197, 94, 0.10)";
const BG_DEL = "rgba(239, 68, 68, 0.10)";

function LineDiffView({ lines }: { lines: DiffLine[] }) {
  return (
    <Box
      sx={{
        fontSize: "11px",
        fontFamily:
          "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        whiteSpace: "pre",
      }}
    >
      {lines.map((line, idx) => {
        const bg =
          line.kind === "add"
            ? BG_ADD
            : line.kind === "del"
              ? BG_DEL
              : "transparent";
        const color =
          line.kind === "add"
            ? COLOR_ADD
            : line.kind === "del"
              ? COLOR_DEL
              : theme.vars.palette.Content.Medium;
        const prefix =
          line.kind === "add" ? "+ " : line.kind === "del" ? "- " : "  ";
        return (
          <Box key={idx} sx={{ backgroundColor: bg, color, px: "4px" }}>
            {prefix}
            {line.text}
          </Box>
        );
      })}
    </Box>
  );
}

export function TurnDiffModal({
  open,
  taskBefore,
  taskAfter,
  onClose,
}: TurnDiffModalProps) {
  const lines = useMemo(
    () =>
      computeLineDiff(
        stableStringify(taskBefore.blocks),
        stableStringify(taskAfter.blocks)
      ),
    [taskBefore, taskAfter]
  );

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        sx={{
          minWidth: 600,
          maxWidth: "min(90vw, 900px)",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography level="title-md" sx={{ mb: 1 }}>
          변경사항
        </Typography>
        <Typography
          level="body-xs"
          sx={{ color: theme.vars.palette.Content.Weak, mb: 1.5 }}
        >
          활동지 blocks JSON 을 줄 단위로 비교해요.
        </Typography>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            p: "10px 12px",
            borderRadius: "6px",
            border: `1px solid ${theme.vars.palette.Border.Weak}`,
            backgroundColor: theme.vars.palette.Background.Level2,
          }}
        >
          <LineDiffView lines={lines} />
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            mt: 1.5,
          }}
        >
          <Button size="sm" variant="Solid" color="neutral" onClick={onClose}>
            확인
          </Button>
        </Box>
      </ModalDialog>
    </Modal>
  );
}
